import { LitElement, html, css } from 'lit';

/**
 * <copy-button value="hello@…"> — a lucide copy icon that copies `value` to the
 * clipboard on click and flips to a lucide check (~1.5s) as feedback. Icon-only;
 * place it beside the value it copies. CSS custom properties pierce shadow DOM so
 * it tracks the page theme. a11y: title + aria-label flip to "Copied!", and an
 * aria-live region announces the copy.
 *
 * NOTE: the attr is `value`, not `text` — camelon's JSX runtime auto-prefixes a
 * BARE set of names (signals/text/show/ref/…) to `data-*` as Datastar sugar, so
 * `text=` would render `data-text=` and Datastar would eval the value as an expr.
 */
const FEEDBACK_MS = 1500;

// lucide icons (https://lucide.dev) — inlined paths, 24×24, stroke=currentColor
const COPY = html`
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
`;
const CHECK = html`
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
`;

export class CopyButton extends LitElement {
  static properties = { value: { type: String }, copied: { state: true } };
  declare value: string;
  declare copied: boolean;
  #timer = 0;

  static styles = css`
    :host {
      display: inline-flex;
    }
    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.25rem;
      border: none;
      border-radius: 6px;
      background: transparent;
      color: var(--fg-dim, #a9b1d6);
      cursor: pointer;
      line-height: 0;
      transition: color 0.12s, background 0.12s;
    }
    button:hover {
      color: var(--cyan, #7dcfff);
      background: var(--panel-hi, #292e42);
    }
    button.copied {
      color: var(--green, #9ece6a);
    }
    svg {
      width: 1.05rem;
      height: 1.05rem;
    }
    .sr {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      white-space: nowrap;
    }
  `;

  constructor() {
    super();
    this.value = '';
    this.copied = false;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
  }

  async #write(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      /* fall through to legacy path (e.g. insecure context / denied) */
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }

  #copy = async (): Promise<void> => {
    const ok = await this.#write(this.value);
    if (!ok) return;
    this.copied = true;
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      this.copied = false;
    }, FEEDBACK_MS);
  };

  render() {
    const label = this.copied ? 'Copied!' : `Copy ${this.value}`;
    return html`
      <button
        class=${this.copied ? 'copied' : ''}
        @click=${this.#copy}
        title=${this.copied ? 'Copied!' : 'Copy'}
        aria-label=${label}
      >
        ${this.copied ? CHECK : COPY}
      </button>
      <span class="sr" aria-live="polite">${this.copied ? 'Copied!' : ''}</span>
    `;
  }
}

customElements.define('copy-button', CopyButton);
